import { FC } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { RosterRow } from "../../../hooks/useCollaboratorRoster";
import { colors } from "../../../styles/colors";

/**
 * Name cell.
 *
 * For a confirmed member the name is a link into the collaborator sheet, which
 * is where a vehicle gets assigned - that was the requested "click the name to
 * open and complete the file" behaviour.
 *
 * For an invitation there is no sheet: no `company_user` row exists until the
 * invitee finishes signing up, so /collaborator/update/{id} would 404. The
 * name is rendered as plain text instead, and falls back to the email when the
 * inviter did not supply a name.
 */
export const CollaboratorNameCell: FC<{ row: RosterRow }> = ({ row }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fullName = `${row.lastname ?? ""} ${row.firstname ?? ""}`.trim();
  const display =
    fullName ||
    row.email ||
    t("collaborator.unnamed", "(nom non renseigné)");

  if (row.status !== "member" || !row.userId) {
    return (
      <span style={{ color: "#8d8d8d" }} title={row.email}>
        {display}
      </span>
    );
  }

  return (
    <span
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/collaborator/update/${row.userId}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/collaborator/update/${row.userId}`);
        }
      }}
      style={{
        color: colors.primary,
        cursor: "pointer",
        fontWeight: 600,
        textDecoration: "underline",
        textUnderlineOffset: 2,
      }}
    >
      {display}
    </span>
  );
};
